import { useEffect, useState } from "react";
import {
  Form,
  Link,
  redirect,
  useActionData,
  useFetcher,
  useLocation,
  useNavigation,
  type ActionFunctionArgs,
  type LoaderFunctionArgs,
} from "react-router";
import { ApiError } from "@repo/api-client";
import type { PasswordLink } from "@repo/shared-types";
import { api } from "@/lib/api";
import { DIVISIONS } from "@/lib/results";
import { forgetEverything } from "@/lib/query";
import { formError, safeNext, type FormError } from "@/lib/session";
import { toast } from "@/lib/toast";
import {
  AuthCard,
  ErrorNote,
  Field,
  PasswordField,
  PasswordManagerUsername,
  SegmentedControl,
  SubmitButton,
} from "@/components/form";
import { useUsernameWarning } from "@/components/username-warning";
import { Button } from "@/components/ui/button";
import { FieldDescription, FieldError } from "@/components/ui/field";
import { Spinner } from "@/components/ui/spinner";

/**
 * The page a sign-up or reset link opens (0030): /sign-up/finish and
 * /reset-password, one page, told apart by the link itself.
 *
 * The token is in the address's fragment, which no server is sent — and
 * which React Router leaves out of a loader's request too. So the page reads
 * it from the location and asks about the link through a fetcher, from
 * /password-link, a route with a loader and no page, the way a conversation
 * reads past its first page. Reading the link never spends it; choosing the
 * password does, and signs in.
 */

export function tokenFromHash(hash: string): string {
  return new URLSearchParams(hash.replace(/^#/, "")).get("token") ?? "";
}

/** What /password-link answers: the link, or that it is no longer one. */
export type LinkLookup = { link: PasswordLink } | { dead: true };

export async function passwordLinkLoader({ request }: LoaderFunctionArgs): Promise<LinkLookup> {
  const token = new URL(request.url).searchParams.get("token") ?? "";
  if (!token) return { dead: true };
  try {
    return { link: await api.auth.passwordLink(token) };
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return { dead: true };
    throw error;
  }
}

type ChooseResult = FormError & { dead?: boolean };

export async function choosePasswordAction({ request }: ActionFunctionArgs): Promise<ChooseResult | Response> {
  const form = await request.formData();
  const purpose = String(form.get("purpose") ?? "");
  const text = (name: string) => String(form.get(name) ?? "").trim() || undefined;

  try {
    await api.auth.choosePassword({
      token: String(form.get("token") ?? ""),
      password: String(form.get("password") ?? ""),
      ...(purpose === "sign_up"
        ? { name: text("name"), username: text("username"), division: text("division") }
        : {}),
    });
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) {
      return { error: error.message, dead: true };
    }
    return formError(error, "Could not save the password");
  }

  // Signed in now, as whoever the link was for: nothing read before is theirs.
  forgetEverything();
  if (purpose === "sign_up") return redirect(safeNext(form.get("next")));
  // Home says nothing of it, and a reset ends every other session.
  const what = form.get("hadPassword") === "true" ? "Password changed." : "Password set.";
  toast(`${what} Every other device has been signed out.`);
  return redirect("/");
}

export default function ChoosePassword() {
  const location = useLocation();
  // Read once: a submission's navigation ends at the form's address, which
  // has no fragment, so a refused password would otherwise lose the token.
  const [token] = useState(() => tokenFromHash(location.hash));
  const lookup = useFetcher<LinkLookup>();
  const result = useActionData() as ChooseResult | undefined;
  const navigation = useNavigation();
  const resetting = location.pathname.startsWith("/reset-password");
  // Asked before a sign-up goes without a username (0076); a reset has no box.
  const warning = useUsernameWarning({
    says: "We'll choose one for you, like user-k3x9q2ab. You can change it any time in Account.",
    accept: "Use a random one",
  });

  useEffect(() => {
    if (token) void lookup.load(`/password-link?token=${encodeURIComponent(token)}`);
    // Once per token; the fetcher revalidates by itself after a submission.
  }, [token]);

  if (!token || result?.dead || (lookup.data && "dead" in lookup.data)) {
    return <DeadLink resetting={resetting} />;
  }
  if (!lookup.data) {
    return (
      <AuthCard title="Checking your link…" footer={null}>
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Spinner />
          One moment.
        </p>
      </AuthCard>
    );
  }

  const { link } = lookup.data;
  const signingUp = link.purpose === "sign_up";
  const title = signingUp ? "Choose a password" : link.hasPassword ? "Choose a new password" : "Set a password";
  const submitting = navigation.state === "submitting";
  const fieldError = (name: string) => (result?.field === name ? result.error : undefined);

  return (
    <AuthCard
      title={title}
      footer={
        <Link to="/sign-in" className="font-medium underline underline-offset-4">
          Back to sign in
        </Link>
      }
    >
      <p className="text-sm text-muted-foreground">
        {signingUp ? "For your new account, " : "For "}
        <strong>{link.email}</strong>.
        {!signingUp && link.hasPassword && " Choosing one signs you out everywhere else."}
        {!signingUp && !link.hasPassword && " You'll be able to sign in with it as well as with Google."}
      </p>
      {result?.error && !result.field && <ErrorNote message={result.error} />}

      <Form method="post" replace className="space-y-4" onSubmit={warning.onSubmit}>
        <input type="hidden" name="token" value={token} />
        <input type="hidden" name="purpose" value={link.purpose} />
        {!signingUp && <input type="hidden" name="hadPassword" value={String(link.hasPassword)} />}
        <input type="hidden" name="next" value={link.next} />
        <PasswordManagerUsername email={link.email} />
        <PasswordField
          label={signingUp || !link.hasPassword ? "Password" : "New password"}
          name="password"
          autoComplete="new-password"
          autoFocus
          error={fieldError("password")}
          hint="At least 8 characters. A few words together are easy to remember and hard to guess."
        />
        {signingUp && (
          <>
            <Field
              label="Name"
              name="name"
              autoComplete="name"
              required={false}
              optional
              maxLength={100}
              error={fieldError("name")}
            />
            <Field
              label="Username"
              name="username"
              // Not "username": that is the email, for password managers.
              autoComplete="nickname"
              required={false}
              pattern="[A-Za-z0-9_\-]{3,30}"
              error={fieldError("username")}
              hint="How people find you, 3 to 30 of a–z, 0–9, - and _."
            />
            <DivisionField error={fieldError("division")} />
          </>
        )}
        <SubmitButton pending={submitting}>
          {submitting ? "Saving…" : signingUp ? "Create account" : "Save password"}
        </SubmitButton>
      </Form>
      {warning.sheet}
    </AuthCard>
  );
}

/**
 * The division, asked once at sign-up and kept in the profile: the one place
 * a result's division comes from. Optional — without one, results stay
 * private, since every leaderboard is men's or women's — which "Not now",
 * first, says without a label saying so (0076). One of three in sight
 * rather than a select: a phone's control. The welcome page asks it too.
 */
export function DivisionField({ error, initial = "" }: { error?: string; initial?: string }) {
  const [division, setDivision] = useState(initial);
  return (
    <div className="space-y-2">
      <SegmentedControl
        label="Division"
        name="division"
        options={[{ value: "", label: "Not now" }, ...DIVISIONS]}
        value={division}
        onChange={setDivision}
      />
      {error && <FieldError>{error}</FieldError>}
      <FieldDescription>
        Which leaderboards your results go on. Without one they stay private; you can set it later in Account.
      </FieldDescription>
    </div>
  );
}

/** A link used already, expired, or never one: and how to get another. */
function DeadLink({ resetting }: { resetting: boolean }) {
  return (
    <AuthCard
      title="This link has expired"
      footer={
        <Link to="/sign-in" className="font-medium underline underline-offset-4">
          Back to sign in
        </Link>
      }
    >
      <p className="text-sm text-muted-foreground">
        It may have been used already — each link works once — or be too old:{" "}
        {resetting ? "a reset link lasts an hour." : "a sign-up link lasts a day."}
      </p>
      <Button asChild size="lg" className="w-full">
        <Link to={resetting ? "/forgot-password" : "/sign-up"}>{resetting ? "Send a new reset link" : "Start again"}</Link>
      </Button>
    </AuthCard>
  );
}
